import { getAnalytics, isSupported } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  signOut as firebaseSignOut,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth';

/**
 * Firebase configuration object
 * @typedef {Object} FirebaseConfig
 * @property {string} apiKey - Firebase API key
 * @property {string} authDomain - Firebase auth domain
 * @property {string} projectId - Firebase project ID
 * @property {string} storageBucket - Firebase storage bucket
 * @property {string} messagingSenderId - Firebase messaging sender ID
 * @property {string} appId - Firebase app ID
 * @property {string} measurementId - Firebase measurement ID
 */

/**
 * Firebase configuration
 * @type {FirebaseConfig}
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate required Firebase config
const validateFirebaseConfig = () => {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
  const missingKeys = requiredKeys.filter((key) => !firebaseConfig[key]);

  if (missingKeys.length > 0) {
    console.error(
      `Missing required Firebase config: ${missingKeys.join(', ')}`
    );
    return false;
  }
  return true;
};

// Initialize Firebase only if config is valid
let app = null;
if (validateFirebaseConfig()) {
  app = initializeApp(firebaseConfig);
} else {
  console.error('Firebase initialization failed due to missing configuration');
}

// Initialize Firebase Authentication and get a reference to the service
export const auth = app ? getAuth(app) : null;

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Configure Google provider
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

/**
 * Firebase Analytics instance - initialized conditionally based on consent
 * @type {import('firebase/analytics').Analytics|null}
 */
let analytics = null;

/**
 * Flag to track if analytics initialization has been attempted
 * @type {boolean}
 */
let analyticsInitAttempted = false;

/**
 * Promise to track ongoing initialization to prevent race conditions
 * @type {Promise<import('firebase/analytics').Analytics|null>|null}
 */
let initializationPromise = null;

/**
 * Initialize Firebase Analytics (only after user consent)
 * Thread-safe: Multiple concurrent calls will return the same promise
 * Call this function only ONCE after user gives cookie consent
 *
 * @returns {Promise<import('firebase/analytics').Analytics|null>} Analytics instance or null
 */
export const initializeAnalytics = async () => {
  // If already initialized, return the existing instance
  if (analytics) {
    return analytics;
  }

  // If initialization is in progress, return the existing promise
  if (initializationPromise) {
    return initializationPromise;
  }

  // If initialization was already attempted and failed, don't retry
  if (analyticsInitAttempted && !analytics) {
    return null;
  }

  // Start new initialization
  initializationPromise = (async () => {
    try {
      analyticsInitAttempted = true;

      // Check if app was initialized
      if (!app) {
        console.warn('Firebase app not initialized');
        return null;
      }

      // Check if analytics is enabled and supported
      const analyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
      const supported = await isSupported();

      if (!analyticsEnabled) {
        if (import.meta.env.MODE === 'development') {
          console.info(
            'Firebase Analytics is disabled via environment variable'
          );
        }
        return null;
      }

      if (!supported) {
        console.warn('Firebase Analytics is not supported in this environment');
        return null;
      }

      // Double-check analytics wasn't initialized while we were checking support
      if (!analytics) {
        analytics = getAnalytics(app);

        if (import.meta.env.MODE === 'development') {
          console.info('Firebase Analytics initialized successfully');
        }
      }

      return analytics;
    } catch (error) {
      console.error('Analytics initialization error:', error);
      return null;
    } finally {
      // Clear the promise after initialization completes (success or failure)
      initializationPromise = null;
    }
  })();

  return initializationPromise;
};

/**
 * Get the Analytics instance (synchronous)
 * Returns null if analytics hasn't been initialized yet
 *
 * @returns {import('firebase/analytics').Analytics|null} Analytics instance or null
 */
export const getAnalyticsInstance = () => analytics;

/**
 * Check if Analytics is initialized
 *
 * @returns {boolean} Whether analytics is initialized
 */
export const isAnalyticsInitialized = () => analytics !== null;

/**
 * Reset analytics instance (useful for testing or re-initialization)
 * WARNING: This should only be used in specific scenarios like user logout
 *
 * @returns {void}
 */
export const resetAnalytics = () => {
  analytics = null;
  analyticsInitAttempted = false;
  initializationPromise = null;
};

// ==================== AUTHENTICATION FUNCTIONS ====================

/**
 * Sign in with Google using popup
 *
 * @returns {Promise<import('firebase/auth').UserCredential>} User credential
 * @throws {Error} Authentication error
 * @example
 * try {
 *   const result = await signInWithGoogle();
 *   console.log('Signed in:', result.user.email);
 * } catch (error) {
 *   console.error('Sign in failed:', error);
 * }
 */
export const signInWithGoogle = async () => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw new Error(`Authentication failed: ${error.message}`);
  }
};

/**
 * Sign out the current user
 *
 * @returns {Promise<void>}
 * @throws {Error} Sign out error
 * @example
 * try {
 *   await signOut();
 *   console.log('User signed out');
 * } catch (error) {
 *   console.error('Sign out failed:', error);
 * }
 */
export const signOut = async () => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw new Error(`Sign out failed: ${error.message}`);
  }
};

/**
 * Listen to authentication state changes
 *
 * @param {function(import('firebase/auth').User|null): void} callback - Callback function
 * @returns {function(): void} Unsubscribe function
 * @example
 * const unsubscribe = onAuthStateChange((user) => {
 *   if (user) {
 *     console.log('User logged in:', user.email);
 *   } else {
 *     console.log('User logged out');
 *   }
 * });
 *
 * // Later, to unsubscribe:
 * unsubscribe();
 */
export const onAuthStateChange = (callback) => {
  if (!auth) {
    console.error('Firebase auth not initialized');
    return () => {}; // Return no-op function
  }

  return onAuthStateChanged(auth, callback);
};

export default app;
